import { useState, useEffect, useCallback, useRef } from 'react'
import { type Difficulty, type CrosswordPuzzle, buildPuzzle } from './crosswordsData'

export type { Difficulty }

export interface CrosswordsState {
  puzzle:               CrosswordPuzzle | null
  foundWordIds:         Set<number>
  dragPath:             number[]           // indices into puzzle.letters
  lastFoundWordId:      number | null      // cleared after 700ms flash
  lastBonusWord:        string | null      // cleared after 700ms flash
  bonusWordSourceIdx:   number | null      // circle letter index where bonus drag ended
  wrongWord:            boolean            // cleared after 650ms shake
  hintsUsed:            number
  hintedCells:          Set<string>        // "row,col" offset-adjusted grid keys
  bonusWordsFound:      string[]
  moves:                number
  startTime:            number | null
  endTime:              number | null
  gameComplete:         boolean
}

function initState(difficulty: Difficulty, wordPool: string[]): CrosswordsState {
  const puzzle = wordPool.length > 0 ? buildPuzzle(wordPool, difficulty) : null
  return {
    puzzle,
    foundWordIds:    new Set(),
    dragPath:        [],
    lastFoundWordId:    null,
    lastBonusWord:      null,
    bonusWordSourceIdx: null,
    wrongWord:          false,
    hintsUsed:       0,
    hintedCells:     new Set(),
    bonusWordsFound: [],
    moves:           0,
    startTime:       null,
    endTime:         null,
    gameComplete:    false,
  }
}

function currentWord(dragPath: number[], letters: string[]): string {
  return dragPath.map(i => letters[i]).join('')
}

function applySubmit(prev: CrosswordsState, wordPool: string[]): CrosswordsState {
  const { puzzle, dragPath, foundWordIds } = prev
  if (!puzzle || dragPath.length < 2) return { ...prev, dragPath: [] }

  const word = currentWord(dragPath, puzzle.letters)
  const startTime = prev.startTime ?? Date.now()

  // Find matching unfound puzzle word
  const match = puzzle.placedWords.find(
    pw => !foundWordIds.has(pw.id) && pw.word === word,
  )

  if (match) {
    const newFound = new Set(foundWordIds)
    newFound.add(match.id)
    const gameComplete = newFound.size === puzzle.placedWords.length
    return {
      ...prev,
      foundWordIds:    newFound,
      dragPath:        [],
      lastFoundWordId: match.id,
      wrongWord:       false,
      moves:           prev.moves + 1,
      startTime,
      gameComplete,
      endTime:         gameComplete ? Date.now() : prev.endTime,
    }
  }

  // Check for bonus word: in theme pool, not a puzzle solution, not already found
  if (word.length >= 2) {
    const placedSet = new Set(puzzle.placedWords.map(pw => pw.word))
    const isThemeWord = wordPool.includes(word)
    const alreadyBonus = prev.bonusWordsFound.includes(word)
    if (isThemeWord && !placedSet.has(word) && !alreadyBonus) {
      return {
        ...prev,
        bonusWordsFound:    [...prev.bonusWordsFound, word],
        lastBonusWord:      word,
        bonusWordSourceIdx: dragPath[dragPath.length - 1] ?? null,
        dragPath:           [],
        wrongWord:          false,
        moves:              prev.moves + 1,
        startTime,
      }
    }
  }

  // Any drag of 2+ letters that isn't a valid word gets shake feedback
  if (word.length >= 2) {
    return { ...prev, dragPath: [], wrongWord: true, moves: prev.moves + 1, startTime }
  }
  return { ...prev, dragPath: [] }
}

function applyRevealHint(prev: CrosswordsState): CrosswordsState {
  const { puzzle, foundWordIds, hintedCells } = prev
  if (!puzzle) return prev

  // Collect unrevealed, un-hinted cells from unfound words
  const candidates: string[] = []
  for (const pw of puzzle.placedWords) {
    if (foundWordIds.has(pw.id)) continue
    const dr = pw.dir === 'V' ? 1 : 0
    const dc = pw.dir === 'H' ? 1 : 0
    for (let i = 0; i < pw.word.length; i++) {
      const r = pw.row + puzzle.rowOffset + i * dr
      const c = pw.col + puzzle.colOffset + i * dc
      const key = `${r},${c}`
      if (!hintedCells.has(key)) candidates.push(key)
    }
  }

  if (candidates.length === 0) return prev

  const chosen = candidates[Math.floor(Math.random() * candidates.length)]
  const newHinted = new Set(hintedCells)
  newHinted.add(chosen)

  return {
    ...prev,
    hintsUsed:   prev.hintsUsed + 1,
    hintedCells: newHinted,
    startTime:   prev.startTime ?? Date.now(),
  }
}

export function useCrosswords(difficulty: Difficulty, wordPool: string[]) {
  const [state, setState] = useState<CrosswordsState>(() => initState(difficulty, wordPool))
  const wordPoolRef = useRef<string[]>(wordPool)
  useEffect(() => { wordPoolRef.current = wordPool }, [wordPool])

  const resetGame = useCallback(() => {
    setState(initState(difficulty, wordPool))
  }, [difficulty, wordPool])

  // Clear found-word flash after 700ms
  useEffect(() => {
    if (state.lastFoundWordId === null) return
    const t = setTimeout(() => setState(prev => ({ ...prev, lastFoundWordId: null })), 700)
    return () => clearTimeout(t)
  }, [state.lastFoundWordId])

  // Clear bonus-word flash + source idx after 700ms
  useEffect(() => {
    if (state.lastBonusWord === null) return
    const t = setTimeout(() => setState(prev => ({ ...prev, lastBonusWord: null, bonusWordSourceIdx: null })), 700)
    return () => clearTimeout(t)
  }, [state.lastBonusWord])

  // Clear wrong-answer shake after 650ms
  useEffect(() => {
    if (!state.wrongWord) return
    const t = setTimeout(() => setState(prev => ({ ...prev, wrongWord: false })), 650)
    return () => clearTimeout(t)
  }, [state.wrongWord])

  const startDrag = useCallback((idx: number) => {
    setState(prev => ({ ...prev, dragPath: [idx] }))
  }, [])

  const extendDrag = useCallback((idx: number) => {
    setState(prev => {
      const path = prev.dragPath
      if (path.length === 0) return prev

      // Going back — undo last letter
      if (path.length >= 2 && path[path.length - 2] === idx) {
        return { ...prev, dragPath: path.slice(0, -1) }
      }
      // Already last letter
      if (path[path.length - 1] === idx) return prev
      // Block reuse of an already-selected letter index (each slot used once)
      if (path.includes(idx)) return prev

      return { ...prev, dragPath: [...path, idx] }
    })
  }, [])

  const endDrag = useCallback(() => {
    setState(prev => applySubmit(prev, wordPoolRef.current))
  }, [])

  const cancelDrag = useCallback(() => {
    setState(prev => ({ ...prev, dragPath: [] }))
  }, [])

  const revealHint = useCallback(() => {
    setState(prev => applyRevealHint(prev))
  }, [])

  return { state, resetGame, startDrag, extendDrag, endDrag, cancelDrag, revealHint }
}
